import type { Disposition } from "@cpos/shared";

export type { Disposition } from "@cpos/shared";

/**
 * Which `out_of_scope` turns a view wants.
 *
 * That one disposition covers two unrelated populations, and until they were
 * split this page claimed every one of them was a question the knowledge base
 * did not cover. The distinction is exact rather than heuristic:
 *
 * - The model *chose* `out_of_scope`, having found nothing in the reference
 *   that bore on the question. `groundFailure` is null. A real gap.
 * - Both draft attempts failed and `runTurn` fell back to `out_of_scope`.
 *   `groundFailure` carries why. Something went wrong on the way to an
 *   answer — a rejected draft, unparseable JSON, or a model that was never
 *   reached. Each is a defect, and writing an FAQ for it fixes nothing.
 *
 * A merchant asking how to charge a customer landed in the second group while
 * the reference answered the question perfectly, and this page reported it as
 * missing documentation.
 */

export type FailureFilter = "exclude" | "only" | "all";

/**
 * The views worth reading back as a gap.
 *
 * `answered` is absent on purpose — it is the normal case and carries no
 * signal. `idv_stuck`, `delete_close` and `wants_human` are deliberate,
 * reviewed exits rather than gaps, so they are not offered either: a rising
 * count there says nothing about the FAQ.
 */

export const GAP_DISPOSITIONS = [
  {
    id: "out_of_scope",
    disposition: "out_of_scope",
    failures: "exclude",
    label: "Unanswered",
    hint: "The assistant had no answer. Each one is a question the knowledge base does not cover.",
  },
  {
    id: "lost_to_failure",
    disposition: "out_of_scope",
    failures: "only",
    label: "Lost to a failure",
    hint: "Every draft attempt failed, so the merchant was handed off. These are defects — a rejected draft, bad JSON, or a model that was never reached — and an FAQ fixes none of them.",
  },
  {
    id: "app_bug",
    disposition: "app_bug",
    failures: "all",
    label: "Reported as a bug",
    hint: "Routed to support as a defect. A rising count can mean a working feature the assistant does not know about.",
  },
  {
    id: "account_change",
    disposition: "account_change",
    failures: "all",
    label: "Account change",
    hint: "Sent to a person because the app cannot do it. A rising count can mean it now can.",
  },
  {
    id: "incentive",
    disposition: "incentive",
    failures: "all",
    label: "Rewards & incentives",
    hint: "Handed off rather than answered. A rising count usually means a live programme is undocumented.",
  },
] as const satisfies readonly {
  id: string;
  disposition: Disposition;
  failures: FailureFilter;
  label: string;
  hint: string;
}[];

export type GapDisposition = (typeof GAP_DISPOSITIONS)[number]["id"];

/** The label and hint for one gap disposition. */
export function gapDispositionCopy(
  id: GapDisposition,
): (typeof GAP_DISPOSITIONS)[number] {
  const found = GAP_DISPOSITIONS.find((entry) => entry.id === id);
  if (!found) throw new Error(`Unknown gap disposition: ${id}`);
  return found;
}

/** Retention on AssistantTurn, enforced by DynamoDB TTL on `expiresAt`. */
export const RETENTION_DAYS = 90;

/** One turn, reduced to the fields this page reads. */
export interface AssistantTurnRow {
  id: string;
  businessId: string;
  question: string;
  disposition: string;
  created: string;
  /**
   * Why grounding rejected a draft, when it did. Null when the disposition
   * was the model's own choice rather than a fallback after failures — which
   * is the whole basis for telling a gap from a defect.
   */
  groundFailure?: string | null;
}

/**
 * Questions that normalised to the same text, across every merchant who asked.
 *
 * `merchants` rather than `occurrences` is what ranks the list: a single
 * merchant asking the same thing five times is one gap, not five.
 */
export interface QuestionGroup {
  /** Normalised form — stable identity of the group, not shown to the reader. */
  key: string;
  /** The most recent spelling, shown verbatim. */
  question: string;
  /** Distinct businesses that asked. */
  merchants: number;
  /** Total turns in the group. */
  occurrences: number;
  /** ISO timestamp of the most recent turn in the group. */
  lastAsked: string;
  /**
   * The distinct failure reasons behind this group, sorted, or empty when
   * none of its turns carried one.
   *
   * Shown rather than merely counted: `unresolved_ref` and
   * `model_unavailable:throttle` are different problems with different
   * owners, and a reader told only that something failed cannot act.
   */
  failureReasons: string[];
}

/** A bounded read of one disposition's turns. */
export interface TurnsPage {
  rows: AssistantTurnRow[];
  /**
   * True when the fetch stopped at its own ceiling rather than at the end of
   * the window, so the counts below are a floor and not a total.
   */
  truncated: boolean;
}
